import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { action, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';

import { Redirect } from 'react-router-dom';
import queryString from 'query-string';

import Alert from '@vromo/ui/components/Alert';

import Tracking from '@vromo/analytics';
import Input from '@vromo/ui/components/Input';
import Logo from '@vromo/icons/logo';
import Button from '@vromo/ui/components/Button';
import Container from '@vromo/ui/components/Container';
import { API, ApiError, ResetPasswordAnswer } from '@vromo/api/api';
import Bugsnag from '@vromo/analytics/bugsnag';
import OverlayablePanel from '@vromo/ui/components/Overlay';
import { qaLabelResetPassword } from '../../../../../../common-qa-labels/reset-password';
import { passwordCheck } from '@vromo/ui/helpers';
import styles from './resetPasswordPage.style';
import { useTranslation } from 'react-i18next';
import { androidStorePage, iOSStorePage, webAppUrl } from '@vromo/api';

const nextStepLinks = [webAppUrl, iOSStorePage, androidStorePage];

const HeaderContent = memo(({ title }: { title: string }) => (
    <div style={styles.headerContainer}>
        <div style={styles.logoContainer}>
            <Logo style={styles.logo} />
        </div>
        <h1 style={styles.pageTitle}>{title}</h1>
    </div>
));

interface PasswordInputProps {
    label: string;
    value: string;
    onChange: JSX.IntrinsicElements['input']['onChange'];
    testId: string;
}

const PasswordInput = memo(({ label, value, onChange, testId }: PasswordInputProps) => (
    <div style={styles.inputContainer}>
        <Input
            label={label}
            type="password"
            placeholder={label}
            value={value}
            onChange={onChange}
            data-testid={testId}
        />
    </div>
));

const ResetPasswordPage = observer(({ location }) => {
    const { t } = useTranslation();

    const token = useMemo(() => queryString.parse(location.search).tok, [location.search]);

    const [state] = useState(
        observable({
            newPassword: '',
            confirmPassword: '',
            errors: [] as { error: boolean; message: string }[],
            shouldRedirect: false,
            successfulUpdate: false
        })
    );
    const [loading, isLoading] = useState(false);

    const saveNewPassword = async () => {
        state.errors = [];

        state.errors.push(...passwordCheck(state.newPassword, state.confirmPassword));

        if (state.errors.length) return;

        try {
            isLoading(true);
            await new API().changePassword.post(
                { password: state.newPassword },
                {},
                token!.toString()
            );

            Tracking.track('Password Changed');
            state.errors.push({
                error: false,
                message: t('resetPassword.successAlert')
            });
            state.newPassword = '';
            state.confirmPassword = '';
            state.successfulUpdate = true;
        } catch (err) {
            const errDetails = err as ApiError<ResetPasswordAnswer>;
            if (errDetails.response.status_code === 400) {
                state.errors.push({
                    error: true,
                    message: `${errDetails.response.status}`
                });
            } else {
                state.errors.push({
                    error: true,
                    message: t('resetPassword.errorAlert')
                });
            }
        } finally {
            isLoading(false);
        }
    };

    useEffect(() => {
        if (!token || token.toString().length < 5) {
            Bugsnag.notify(new Error('Invalid token in reset password link'));
            state.shouldRedirect = true;
        }
    }, [token, state]);

    const onChangeNewPassword = useCallback(
        action((e) => (state.newPassword = e.target.value)),
        [state.newPassword]
    );

    const onChangeConfirmPassword = useCallback(
        action((e) => (state.confirmPassword = e.target.value)),
        [state.confirmPassword]
    );

    if (state.shouldRedirect) {
        return <Redirect to="/" />;
    }

    return (
        <div style={styles.container}>
            <HeaderContent title={t('resetPassword.title')} />
            <div style={styles.pageContainer}>
                {!state.successfulUpdate ? (
                    <OverlayablePanel
                        style={styles.panelContainer}
                        active={loading}
                        loadingText={t('resetPassword.loading')}>
                        <Container style={styles.contentContainer}>
                            {state.errors.map((error, ix) => (
                                <Alert
                                    data-testid={qaLabelResetPassword.errorMessage}
                                    key={ix}
                                    success={!error.error}
                                    danger={error.error}>
                                    <p dangerouslySetInnerHTML={{ __html: error.message }} />
                                </Alert>
                            ))}
                            <PasswordInput
                                label={t('resetPassword.newPassword')}
                                value={state.newPassword}
                                onChange={onChangeNewPassword}
                                testId={qaLabelResetPassword.newPasswordInput}
                            />
                            <PasswordInput
                                label={t('resetPassword.confirmPassword')}
                                value={state.confirmPassword}
                                onChange={onChangeConfirmPassword}
                                testId={qaLabelResetPassword.confirmNewPasswordInput}
                            />
                            <div style={styles.resetButtonContainer}>
                                <Button
                                    primary
                                    style={styles.resetButton}
                                    onClick={() => runInAction(saveNewPassword)}
                                    disabled={loading}
                                    data-testid={qaLabelResetPassword.submitButton}>
                                    {t('resetPassword.submit')}
                                </Button>
                            </div>
                        </Container>
                    </OverlayablePanel>
                ) : (
                    <Container style={styles.successDialogContainer}>
                        <h3 data-testid={qaLabelResetPassword.passwordChangeSuccessTitleMessage}>
                            {t('resetPassword.successMessage')}
                        </h3>
                        <div
                            data-testid={qaLabelResetPassword.passwordChangeSuccessSubtitleMessage}>
                            {t('resetPassword.nextSteps')
                                .split('*')
                                .map((text, index) =>
                                    index % 2 === 0 ? (
                                        text
                                    ) : (
                                        <a href={nextStepLinks[Math.floor(index / 2)]}>{text}</a>
                                    )
                                )}
                        </div>
                    </Container>
                )}
            </div>
        </div>
    );
});

export default ResetPasswordPage;
